import React, { ReactNode, useEffect, useState } from 'react';
import clsx from 'clsx';
import css from './ModalDialog.module.css';
import { ReactPortal } from '../../reactPortal';
import { H2 } from '../../text';
import { CloseImage } from '../../../assets/images';
import { Image } from '../../images';
import { useKeyPress } from '@mchs/web-core';
import { useResponsiveSize } from '../../../../hooks/useResponsiveSize';
import { ButtonType, TextButton } from '../../buttons';

export interface ModalButton {
  caption: string;
  onClick: () => void;
}

export interface ModalProps extends ModalBaseProps {
  fullScreen?: boolean;
  showCloseButton?: boolean;
  onClose?: () => void;
  button1?: ModalButton;
  button2?: ModalButton;
}

export interface ModalBaseProps {
  isOpen: boolean;
  title: string;
  children: ReactNode;
  maxWidth?: number;
}

const defaultMaxWidth = '80%';

const ModalDialog: React.FC<ModalProps> = (props: ModalProps) => {
  const {isNarrowWidth, screenSize} = useResponsiveSize();
  const [maxWidth, setMaxWidth] = useState<string>(defaultMaxWidth);

  const handleClose = () => {
    if (props.onClose) {
      props.onClose();
    }
  };

  const handleEscapeKey = () => {
    handleClose();
  };

  useKeyPress([{ key: 'Escape' }], handleEscapeKey);

  const renderHeader = (): ReactNode => {
    return (
      <>
        <div className={css.title}>
          <H2 text={props.title} />
        </div>
        {props.showCloseButton && (
          <div>
            <button className={css.closeButton} onClick={handleClose}>
              <Image
                source={CloseImage}
                alternateText="Close"
                height={14}
                width={14}
              />
            </button>
          </div>
        )}
      </>
    );
  };

  const renderFooter = (): ReactNode => {
    return props.button1 ? (
      <div
        className={clsx(
          css.footer,
          props.button1 && props.button2 ? css.stackedFooter : null,
          props.fullScreen && isNarrowWidth ? css.narrowFooter : null
        )}
      >
        <>
          <TextButton
            caption={props.button1.caption}
            onClick={props.button1.onClick}
          />
          {props.button2 && (
            <TextButton
              buttonType={ButtonType.Tertiary}
              caption={props.button2.caption}
              onClick={props.button2.onClick}
            />
          )}
        </>
      </div>
    ) : null;
  };

  useEffect(() => {
    if (props.fullScreen && isNarrowWidth) {
      setMaxWidth('100%');
    } else {
      if (props.maxWidth && props.maxWidth > 0) {
        let workingMaxWidth = props.maxWidth;
        if (workingMaxWidth < 200) {
          workingMaxWidth = 200;
        }
        const percentWidth = screenSize.width * 0.8;
        if (percentWidth > workingMaxWidth) {
          setMaxWidth(`${workingMaxWidth}px`);
        } else {
          setMaxWidth(defaultMaxWidth);
        }
      } else {
        setMaxWidth(defaultMaxWidth);
      }
    }
  }, [props.fullScreen, isNarrowWidth, props.maxWidth, screenSize]);

  if (!props.isOpen) {
    return null;
  }

  return (
    <ReactPortal wrapperId="react-portal-modal-container">
      <div className={css.container}>
        <div
          className={clsx(
            css.modal,
            isNarrowWidth && props.fullScreen ? css.fullScreen : null
          )}
          style={{ maxWidth: maxWidth }}
        >
          <div className={css.header}>{renderHeader()}</div>
          <div className={css.content}>{props.children}</div>
          {renderFooter()}
        </div>
      </div>
    </ReactPortal>
  );
};

export default ModalDialog;
